import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Loader2, CheckCircle, XCircle, Pencil } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';

interface LeaderboardEntry {
  phone: string;
  rawPhone?: string;
  displayName: string;
  testimonial: string;
  directRefs: number;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

const statusLabel: Record<string, string> = {
  pending: '待審核',
  approved: '已批准',
  rejected: '已拒絕',
};

const statusClass: Record<string, string> = {
  pending: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  approved: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  rejected: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
};

const AdminLeaderboard: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const { can } = usePermissions();
  const canWriteCms = can(PERMISSIONS.CMS_WRITE);

  useEffect(() => {
    fetchItems();
  }, [filter]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      setError('');
      const query = filter !== 'all' ? `?status=${filter}` : '';
      const res = await apiFetch(`/api/public/admin/leaderboard${query}`);
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : data.items || []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '獲取列表失敗');
      }
    } catch {
      setError('獲取列表失敗');
    } finally {
      setLoading(false);
    }
  };

  const [editingItem, setEditingItem] = useState<LeaderboardEntry | null>(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editTestimonial, setEditTestimonial] = useState('');
  const [saving, setSaving] = useState(false);

  const handleApprove = async (item: LeaderboardEntry) => {
    if (!item.rawPhone) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/public/admin/leaderboard/${encodeURIComponent(item.rawPhone)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicApproved: 1 }),
      });
      if (!res.ok) throw new Error('Update failed');
      await fetchItems();
    } catch (err) {
      setError('批准失敗');
    } finally {
      setSaving(false);
    }
  };

  const handleReject = async (item: LeaderboardEntry) => {
    if (!item.rawPhone) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/public/admin/leaderboard/${encodeURIComponent(item.rawPhone)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ publicApproved: -1 }),
      });
      if (!res.ok) throw new Error('Update failed');
      await fetchItems();
    } catch (err) {
      setError('拒絕失敗');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item: LeaderboardEntry) => {
    setEditingItem(item);
    setEditDisplayName(item.displayName || '');
    setEditTestimonial(item.testimonial || '');
  };

  const handleSaveEdit = async () => {
    if (!editingItem?.rawPhone) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/public/admin/leaderboard/${encodeURIComponent(editingItem.rawPhone)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName: editDisplayName, testimonial: editTestimonial }),
      });
      if (!res.ok) throw new Error('Update failed');
      setEditingItem(null);
      await fetchItems();
    } catch (err) {
      setError('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout currentPage="leaderboard">
      <div>
        {error && (
          <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-900/30 text-red-700 dark:text-red-400 flex justify-between items-center">
            <span>{error}</span>
            <button onClick={() => setError('')} className="hover:opacity-70">✕</button>
          </div>
        )}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-lg font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <Award className="text-brand-600" size={22} />
              好餸傳愛榜
            </h2>
            <div className="flex items-center gap-2">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((key) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    filter === key
                      ? 'bg-brand-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {key === 'all' ? '全部' : statusLabel[key]}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Loader2 size={24} className="animate-spin mx-auto mb-2" />
              加載中...
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-gray-500 dark:text-gray-400">
              <Award size={48} className="mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p>暫無待審核感言</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-700 dark:text-gray-300">
                  <tr>
                    <th className="px-4 py-3 text-sm font-semibold">顯示名稱</th>
                    <th className="px-4 py-3 text-sm font-semibold">遮罩電話</th>
                    <th className="px-4 py-3 text-sm font-semibold">感言</th>
                    <th className="px-4 py-3 text-sm font-semibold">直接推薦數</th>
                    <th className="px-4 py-3 text-sm font-semibold">狀態</th>
                    <th className="px-4 py-3 text-sm font-semibold text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {items.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-900/30">
                      <td className="px-4 py-3 font-medium text-gray-800 dark:text-white">{item.displayName || '-'}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{item.phone}</td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                        <p className="truncate">{item.testimonial || '-'}</p>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">{item.directRefs}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusClass[item.status]}`}>
                          {statusLabel[item.status]}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {item.status === 'pending' && canWriteCms && (
                            <>
                              <button
                                onClick={() => handleApprove(item)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                              >
                                <CheckCircle size={14} />
                                批准
                              </button>
                              <button
                                onClick={() => handleReject(item)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                              >
                                <XCircle size={14} />
                                拒絕
                              </button>
                            </>
                          )}
                          <button
                            onClick={() => openEdit(item)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-brand-700 dark:text-brand-400 bg-brand-50 dark:bg-brand-900/20 rounded hover:bg-brand-100 dark:hover:bg-brand-900/30 transition-colors"
                          >
                            <Pencil size={14} />
                            編輯
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 w-full max-w-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-800 dark:text-white">編輯感言</h3>
              <button
                onClick={() => setEditingItem(null)}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">顯示名稱</label>
                <input
                  type="text"
                  value={editDisplayName}
                  onChange={(e) => setEditDisplayName(e.target.value)}
                  placeholder="好餸粉絲"
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">感言</label>
                <textarea
                  value={editTestimonial}
                  onChange={(e) => setEditTestimonial(e.target.value)}
                  rows={4}
                  placeholder="輸入感言內容..."
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-800 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => setEditingItem(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                取消
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 rounded-lg transition-colors disabled:opacity-60"
              >
                {saving && <Loader2 size={16} className="animate-spin" />}
                儲存
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminLeaderboard;
